import { Tabs, Toolbar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from 'App';
import * as S from './AnomaliesSidebar.styled';
import {
  ManualAnomalies,
  SuspectedAnomalies,
  RejectedAnomalies,
  ConfirmedAnomalies,
  TabPanel,
} from './components';
import { SidebarPropsType } from './AnomaliesSidebar.types';
import { convertMillisecondsToSeconds } from '@shared';

export function AnomaliesSidebar(props: SidebarPropsType) {
  const {
    onSeekChange,
    playedSeconds,
    auditUuid,
    anomalies,
    refetchAnomalies,
    disabled = false,
    isReview = false,
    setPlaying = () => null,
  } = props;

  const [tabIndex, setTabIndex] = useState(1);

  const { data: anomalyTypes } = useQuery(['anomaly types'], () => auditApi.getAnomalyTypes(), {
    staleTime: 1000 * 60 * 60 * 24,
  });

  const enabledAnomalyTypes = anomalyTypes
    ?.filter(({ enabled }) => enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const { data: processingRegions } = useQuery(
    ['processing regions', auditUuid],
    () => auditApi.getProcessingRegions(auditUuid),
    {
      select: (data) =>
        data.map((region) => ({
          ...region,
          start: convertMillisecondsToSeconds(region.start),
          end: convertMillisecondsToSeconds(region.end),
          suspectPercent: `${(region.suspectPercent * 100).toFixed(2)}%`,
        })),
    }
  );

  const { data: rejectedRegions } = useQuery(
    ['rejected regions', auditUuid],
    () => auditApi.getRejectedRegions(auditUuid),
    {
      select: (data) =>
        data.map((region) => ({
          ...region,
          start: convertMillisecondsToSeconds(region.start),
          end: convertMillisecondsToSeconds(region.end),
        })),
    }
  );

  return (
    <S.Drawer variant='permanent' anchor='right'>
      <Toolbar />
      <Tabs
        sx={{ background: '#fff' }}
        value={tabIndex}
        variant='fullWidth'
        onChange={(e, value) => setTabIndex(value)}
      >
        <S.Tab value={1} icon={<SearchIcon />} />
        <S.Tab
          value={2}
          icon={
            <S.StyledBadge
              badgeContent={processingRegions?.length}
              invisible={!processingRegions}
              mode='orange'
            >
              <HelpOutlineIcon />
            </S.StyledBadge>
          }
        />
        <S.Tab
          value={3}
          icon={
            <S.StyledBadge badgeContent={anomalies?.length} invisible={!anomalies} mode='green'>
              <CheckCircleOutlineIcon />
            </S.StyledBadge>
          }
        />
        <S.Tab
          value={4}
          icon={
            <S.StyledBadge
              badgeContent={rejectedRegions?.length}
              invisible={!rejectedRegions}
              mode='grey'
            >
              <CancelOutlinedIcon />
            </S.StyledBadge>
          }
        />
      </Tabs>
      <TabPanel value={tabIndex} index={1}>
        <ManualAnomalies
          refetch={refetchAnomalies}
          anomalyTypes={enabledAnomalyTypes}
          playedSeconds={playedSeconds}
          auditUuid={auditUuid}
          disabled={disabled}
          setPlaying={setPlaying}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <SuspectedAnomalies
          suspectedRegions={processingRegions}
          refetch={() => refetchAnomalies()}
          anomalyTypes={enabledAnomalyTypes}
          onSeekChange={onSeekChange}
          auditUuid={auditUuid}
          disabled={disabled}
          showSuspectPercent={isReview}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={3}>
        <ConfirmedAnomalies
          onSeekChange={onSeekChange}
          anomalies={anomalies}
          anomalyTypes={enabledAnomalyTypes}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={4}>
        <RejectedAnomalies
          rejectedRegions={rejectedRegions}
          refetch={refetchAnomalies}
          anomalyTypes={anomalyTypes}
          onSeekChange={onSeekChange}
          auditUuid={auditUuid}
          disabled={disabled}
        />
      </TabPanel>
    </S.Drawer>
  );
}
